// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { Box, Icon, Tooltip, Typography } from "@outschool/backpack";
import {
  OneOnOneClassroomEducatorViewSidebarQuery,
  TeacherSectionQuery,
} from "@outschool/gql-frontend-generated";
import { faQuestionCircle } from "@outschool/icons";
import { activityAvailabilityPath } from "@outschool/routes";
import { ExecutionResult, gql, useMutation } from "@outschool/ui-apollo";
import {
  Modal,
  ModalActionButtons,
  Select,
} from "@outschool/ui-legacy-component-library";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import * as User from "../../../shared/User";
import { useGetSellerOrgTeacherScheduleSettings } from "../../hooks/SellerOrgTeacherSchedules";
import { useSellerOrgTeachers } from "../../queries/SellerOrgQueries";
import { hasCompletedTrainings } from "../section/SectionAssignTeacherSelect";

const sectionFragment = gql`
  fragment SellerOrgSectionReassignModal_section on Section {
    uid
    leader {
      uid
      name
    }
  }
`;

const sectionCacheQuery = gql`
  query SellerOrgSectionReassignModal_section($sectionUid: ID!) {
    section(uid: $sectionUid) {
      ...SellerOrgSectionReassignModal_section
    }
  }
  ${sectionFragment}
`;

const updateSectionMutation = gql`
  mutation SellerOrgSectionReassignModal_updateSection(
    $sectionUid: ID!
    $updates: LeaderSectionInput
  ) {
    updateSection(uid: $sectionUid, updates: $updates) {
      ...SellerOrgSectionReassignModal_section
    }
  }
  ${sectionFragment}
`;

export default function SellerOrgSectionReassignModal({
  section,
  learnerName,
  orgTeacherAssignedUid,
  isOpen,
  onClose,
}: {
  section:
    | OneOnOneClassroomEducatorViewSidebarQuery["section"]
    | TeacherSectionQuery["section"];
  learnerName: string;
  orgTeacherAssignedUid: string;
  isOpen: boolean;
  onClose(): void;
}) {
  const [orgTeacherUid, setOrgTeacherUid] = React.useState<string>(
    orgTeacherAssignedUid,
  );
  const { teachers: sellerOrgTeachers } = useSellerOrgTeachers();
  const Link = useLinkComponent();
  const { loading, scheduleSettings } = useGetSellerOrgTeacherScheduleSettings(
    section?.activity?.uid!,
  );
  const [updateSection] = useMutation(updateSectionMutation, {
    update: (cache, result: ExecutionResult<any>) => {
      if (!!result?.data?.updateSection) {
        cache.writeQuery<any, any>({
          query: sectionCacheQuery,
          data: { section: result?.data?.updateSection },
          variables: { sectionUid: section?.uid },
        });
      }
    },
  });

  const sellerOrgTeacherOptions = React.useMemo(() => {
    return [
      {
        label: section?.activity?.leader?.name,
        value: null,
        disabled: false,
      },
      ...sellerOrgTeachers
        .filter(
          so =>
            !section?.activity.hasTeacherSchedule ||
            scheduleSettings.find(ss => ss.teacher?.uid === so.uid),
        )
        .sort((a, b) =>
          !a.name ? 1 : !b.name ? -1 : a.name.localeCompare(b.name),
        )
        .map(t => ({
          label: t.name,
          value: t.uid,
          disabled:
            User.hasBackgroundCheckExpired(t) ||
            !t.backgroundChecked ||
            !t.hasVerifiedIdentity ||
            !hasCompletedTrainings(t),
        })),
    ];
  }, [scheduleSettings, section, sellerOrgTeachers]);

  const handleTeacherSelected = React.useCallback(
    async (orgTeacherUid: string) => {
      await updateSection({
        variables: {
          sectionUid: section?.uid,
          updates: { user_uid: orgTeacherUid },
        },
      });
      onClose();
    },
    [updateSection, section, onClose],
  );

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Typography variant="h3" sx={{ marginBottom: "0.5em" }}>
        {`Teacher for ${learnerName}`}
      </Typography>
      <Box>
        <Typography>
          Re-assign this learner to a new teacher. Once assigned, this new
          teacher will be able to view, teach, and manage meetings for this
          learner.
        </Typography>
      </Box>
      <Box sx={{ marginY: "1.5em" }}>
        <Box
          flex
          sx={{ gap: "0.5em", alignItems: "center", paddingBottom: "0.5em" }}
        >
          <Typography variant="subtitle1">Select a teacher</Typography>
          <Tooltip
            sx={{ gridArea: "badge", width: "fit-content" }}
            title={
              <Typography>
                The list displays only those teachers within your organization
                whom you have indicated are able to teach this class. Want to
                add a teacher to this drop down? Visit the{" "}
                <Link
                  to={activityAvailabilityPath({
                    uid: section?.activity?.uid!,
                  })}
                >
                  Manage Class Page.
                </Link>
              </Typography>
            }
            placement="top"
            leaveDelay={200}
          >
            <Icon icon={faQuestionCircle} />
          </Tooltip>
        </Box>
        <Select
          value={orgTeacherUid}
          onChange={setOrgTeacherUid}
          disabled={loading}
          handleChangeOnBlur={false}
          options={sellerOrgTeacherOptions}
          sx={{ width: 500 }}
        />
      </Box>
      <ModalActionButtons
        onPrimary={() => handleTeacherSelected(orgTeacherUid)}
        onCancel={onClose}
        primaryLabel="Confirm"
        sx={{ flexDirection: "row" }}
      />
    </Modal>
  );
}
